import PropTypes from "prop-types";
import { useMemo } from "react";
import { withALErrorBoundary } from "../helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";
import useManyCheckInventory from "../hooks/useManyCheckInventory";
import ProductAlternativeColor from "./productAlternatives/ProductAlternativeColor";
import ProductAlternativeTextBlock from "./productAlternatives/ProductAlternativeTextBlock";
import ProductAlternativeDropdownBlock from "./productAlternatives/ProductAlternativeDropdownBlock";
import { ComponentType } from "../constants/ComponentType";

const ALTERNATIVES_NAME = {
  COLOR: "Color",
  METAL: "Metal",
  CARAT_WEIGHT: "Carat Weight",
  STONES: "Number of Stones",
};

const ALTERNATIVES_ORDER = ["COLOR", "METAL", "DEFAULT"];

function getAlternativeNameOrder(attrName) {
  const key = Object.entries(ALTERNATIVES_NAME).find(([, value]) => value === attrName)?.[0];
  return ALTERNATIVES_ORDER.includes(key)
    ? ALTERNATIVES_ORDER.indexOf(key)
    : ALTERNATIVES_ORDER.indexOf("DEFAULT");
}

function getFormattedAttributesFromAlternative(alternative, inventoryData) {
  const inventoryDataIsLoading = inventoryData.isLoading;
  const inventoryDataIsFound = inventoryData?.isFound;
  const inventoryDataIsAvailableForSale = inventoryData?.availableForSale;
  const separator = /\s*-\s*/;

  // Split by - with or without spacing around it
  return alternative.attributes?.reduce((acc, attr) => {
    const [attrName, ...attrArgs] = attr.split(separator);

    acc[attrName] = {
      attrName,
      attrArgs,
      config: attr,
      handle: alternative.handle,
      isFound: inventoryDataIsLoading ? true : inventoryDataIsFound,
      availableForSale: inventoryDataIsLoading ? true : inventoryDataIsAvailableForSale,
    };
    return acc;
  }, {});
}

function areAttributesEqual(attrArgsA, attrArgsB) {
  return (
    attrArgsA.length === attrArgsB.length &&
    attrArgsA.every((attrArg, idx) => attrArg === attrArgsB[idx])
  );
}

function countSimilarAttributes(originalAlternative, alternative) {
  return Object.entries(originalAlternative).reduce(
    (acc, [key, value]) =>
      areAttributesEqual(value.attrArgs, alternative[key]?.attrArgs || []) ? acc + 1 : acc,
    0
  );
}

function ProductAlternatives({
  handle,
  productAlternatives,
  componentType,
  handleAlternativeClick,
  stepPrefix, // used to show multi step indicator
  tags = [],
  mtoSettings,
}) {
  const alternatives = useMemo(
    () => productAlternatives?.alternatives || [],
    [productAlternatives]
  );
  const productsData = useManyCheckInventory(alternatives);

  // alternativesByAttributes = { "Color": [...], "Pendant": [...], "Choose a product": [...] }
  const alternativesByAttributes = useMemo(() => {
    const formattedAlternativesByAttribute = {};
    const pageProductAlternativeIndex = alternatives.findIndex((alt) => alt.handle === handle);
    if (pageProductAlternativeIndex === -1) return null;
    const formattedAlternativesByHandle = {
      [handle]: getFormattedAttributesFromAlternative(
        alternatives[pageProductAlternativeIndex],
        productsData[pageProductAlternativeIndex]
      ),
    };
    const pageProductAttributes = formattedAlternativesByHandle[handle];

    alternatives?.forEach((alt, idx) => {
      const alternativeAttribute = getFormattedAttributesFromAlternative(alt, productsData[idx]);
      formattedAlternativesByHandle[alt.handle] = alternativeAttribute;
      Object.entries(alternativeAttribute).forEach(([key, value]) => {
        // Search for a similar alternative, if one with identical attribute exist,
        // we take the one that match the page prodruct the closest.
        const competingAttributeIndex = (formattedAlternativesByAttribute[key] || []).findIndex(
          (formattedAlt) => areAttributesEqual(formattedAlt.attrArgs, value.attrArgs)
        );

        if (competingAttributeIndex === -1) {
          formattedAlternativesByAttribute[key] = [
            ...(formattedAlternativesByAttribute[key] || []),
            value,
          ];
        } else {
          const competingAttributeSimilarities = countSimilarAttributes(
            pageProductAttributes,
            formattedAlternativesByHandle[
              formattedAlternativesByAttribute[key][competingAttributeIndex].handle
            ]
          );
          const currentAttributeSimilarities = countSimilarAttributes(
            pageProductAttributes,
            alternativeAttribute
          );
          if (currentAttributeSimilarities > competingAttributeSimilarities) {
            formattedAlternativesByAttribute[key][competingAttributeIndex] = value;
          }
        }
      });
    });

    // Remove color and metal if there is only one option for each on PLP
    const colorAlternatives = formattedAlternativesByAttribute[ALTERNATIVES_NAME.COLOR];
    const metalAlternatives = formattedAlternativesByAttribute[ALTERNATIVES_NAME.METAL];
    if (
      (componentType === ComponentType.CP || componentType === ComponentType.CP_LAZY) &&
      colorAlternatives?.length === 1 &&
      metalAlternatives?.length === 1
    ) {
      delete formattedAlternativesByAttribute[ALTERNATIVES_NAME.METAL];
      delete formattedAlternativesByAttribute[ALTERNATIVES_NAME.COLOR];

      if (Object.keys(formattedAlternativesByAttribute).length === 0) {
        return null;
      }
    }

    return formattedAlternativesByAttribute;
  }, [productsData, alternatives, handle, componentType]);

  if (!alternativesByAttributes || alternatives.length === 1) {
    return null;
  }

  const materialOrder = ["Gold Vermeil", "10K Solid Gold"];

  return (
    <div
      key={`${handle}-alternatives`}
      className={cn(
        "product-alternatives w-full mb-[30px] p-0 mobile:px-[15px]",
        componentType === ComponentType.CP && "mobile:px-0 mb-0"
      )}
    >
      {Object.entries(alternativesByAttributes)
        .sort(
          ([attrNameA], [attrNameB]) =>
            getAlternativeNameOrder(attrNameA) - getAlternativeNameOrder(attrNameB)
        )
        .map(([attrName, alternativesOfAttr]) => {
          if (attrName === ALTERNATIVES_NAME.COLOR || attrName === ALTERNATIVES_NAME.METAL) {
            return (
              <ProductAlternativeColor
                key={attrName}
                pageProductHandle={handle}
                alternatives={alternativesOfAttr}
                handleAlternativeClick={handleAlternativeClick}
                componentType={componentType}
                attrName={attrName}
              />
            );
          }
          if (componentType !== ComponentType.CP && componentType !== ComponentType.CP_LAZY) {
            if (attrName === ALTERNATIVES_NAME.CARAT_WEIGHT) {
              return (
                <ProductAlternativeDropdownBlock
                  key={attrName}
                  pageProductHandle={handle}
                  alternatives={alternativesOfAttr}
                  labelTitle={attrName}
                  stepPrefix={stepPrefix}
                />
              );
            }
            return (
              <ProductAlternativeTextBlock
                key={attrName}
                pageProductHandle={handle}
                alternatives={alternativesOfAttr.toSorted((a, b) => {
                  const ai = materialOrder.indexOf(a.attrArgs[0]);
                  const bi = materialOrder.indexOf(b.attrArgs[0]);

                  // If neither is in the list, preserve original order
                  if (ai === -1 && bi === -1) return 0;
                  // Unknown values go to the end
                  if (ai === -1) return 1;
                  if (bi === -1) return -1;

                  return ai - bi;
                })}
                labelTitle={attrName}
                stepPrefix={stepPrefix}
                componentType={componentType}
                tags={tags}
                mtoSettings={mtoSettings}
              />
            );
          }
          return null;
        })}
    </div>
  );
}

ProductAlternatives.propTypes = {
  handle: PropTypes.string.isRequired,
  productAlternatives: PropTypes.shape({
    alternatives: PropTypes.arrayOf(
      PropTypes.shape({
        attributes: PropTypes.arrayOf(PropTypes.string).isRequired,
        handle: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  componentType: PropTypes.string,
  handleAlternativeClick: PropTypes.func,
  stepPrefix: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  mtoSettings: PropTypes.objectOf(
    PropTypes.shape({
      variants: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          isSelected: PropTypes.bool.isRequired,
        })
      ),
    })
  ),
};

export default withALErrorBoundary({
  name: "ProductAlternatives",
  priority: "P2",
})(ProductAlternatives);
